import { STIPPLE_SIZE, STIPPLE_DOT_RADIUS } from "./stipple.js";

// Intl.NumberFormat construction is expensive enough to show up in CPU
// profiles when called per row per canvas draw (ScopeColumns entry
// counts) — keep one instance per digit setting.
const _compactFormats = new Map();

export function formatCompact(value) {
  const digits = value > 999 ? 1 : 0;
  let format = _compactFormats.get(digits);
  if (!format) {
    format = new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: digits,
    });
    _compactFormats.set(digits, format);
  }
  return format.format(value);
}

export function formatPercent(value, digits = 0) {
  return `${Number(value).toFixed(digits)}%`;
}

// Compact USD for estimated token cost. Sub-cent rounds to "<$0.01" (so a
// real-but-tiny spend never shows as "$0.00" and reads as free); under $1k
// shows cents; $1k+ goes compact ("$1.2K", "$3.4M") to stay on one line in a
// chip/legend. Pass a non-finite/zero value through as "$0".
export function formatUsd(value) {
  const v = Number(value);
  if (!Number.isFinite(v) || v === 0) return "$0";
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs < 0.01) return `${sign}<$0.01`;
  if (abs < 1000) return `${sign}$${abs.toFixed(2)}`;
  return `${sign}$${formatCompact(abs)}`;
}

export function formatSigned(value, digits = 1) {
  return `${value > 0 ? "+" : ""}${Number(value).toFixed(digits)}%`;
}

export function computeSurfacePoints(series, width, height, padding, minValue = 0, maxValue = null) {
  return seriesPoints(series, width, height, padding, minValue, maxValue);
}

export function computeSurfaceDomain(opts) {
  return resolveSurfaceDomain(opts);
}

export function sumLayers(layers) {
  return sumStackedLayers(layers || []);
}

// Collapse a per-bucket numeric series (e.g. the activity-bundle `total`
// sparkline) to a single scalar. Tolerates a non-array (returns 0) and
// non-finite buckets so callers can hand it raw bundle output without the
// result ever coming back NaN (O-1843).
export function sumSeries(series) {
  if (!Array.isArray(series)) return 0;
  return series.reduce((sum, n) => sum + (Number.isFinite(Number(n)) ? Number(n) : 0), 0);
}

function seriesPoints(series, width, height, padding, minValue = 0, maxValue = null) {
  const min = minValue;
  const max = maxValue ?? Math.max(...series);
  const usableWidth = width - padding * 2;
  const usableHeight = height - padding * 2;
  return series.map((value, index) => {
    const x = padding + (index / Math.max(1, series.length - 1)) * usableWidth;
    const y =
      padding + usableHeight - ((value - min) / Math.max(1, max - min || 1)) * usableHeight;
    return { x, y };
  });
}

function sumStackedLayers(layers) {
  return layers.reduce((sum, layer) => {
    if (!sum.length) return [...layer.series];
    return sum.map((value, index) => value + layer.series[index]);
  }, []);
}

function resolveSurfaceDomain({ series, layers, domainMin = 0, domainMax = null }) {
  const maxFromSeries = series?.length ? Math.max(...series) : 0;
  const maxFromLayers = layers?.length ? Math.max(...sumStackedLayers(layers), 1) : 0;
  return {
    min: domainMin,
    max: domainMax ?? Math.max(maxFromSeries, maxFromLayers, 1),
  };
}

// Theme-keyed cache for the computed-style reads below. Reading a live
// CSSStyleDeclaration (getComputedStyle().getPropertyValue / .color)
// forces a synchronous style recalc whenever styles are dirty. During a
// panel-resize drag every chart rebuild re-read ~a dozen vars against a
// just-mutated DOM, and those forced recalcs dominated the renderer
// profile (550ms of a 5s drag). The values only change with the theme,
// so cache per data-theme and let a flip repopulate lazily.
//
// Assumes `styles` is the documentElement computed style — every
// current caller passes that. A per-element caller would need its own
// cache key scheme; don't route one through here.
const _themeColorCache = new Map();
let _themeColorCacheTheme = null;

function cachedThemeColor(key, compute) {
  const theme =
    typeof document !== "undefined"
      ? document.documentElement.dataset.theme || "light"
      : "light";
  if (theme !== _themeColorCacheTheme) {
    _themeColorCache.clear();
    _themeColorCacheTheme = theme;
  }
  if (_themeColorCache.has(key)) return _themeColorCache.get(key);
  const value = compute();
  // Never cache a falsy resolution: a transient early read (chart drawn
  // before the theme vars apply) resolves to "" — caching it would pin
  // the empty value until the next theme flip, whereas the pre-cache
  // behavior self-healed on the next read.
  if (value) _themeColorCache.set(key, value);
  return value;
}

export function resolveCanvasColor(value, styles) {
  if (!value) return value;
  const trimmed = value.trim();
  if (trimmed === "currentColor") {
    return cachedThemeColor("currentColor", () => styles.color) || trimmed;
  }
  if (!trimmed.startsWith("var(")) return trimmed;

  const match = trimmed.match(/^var\((--[^,\s)]+)(?:,\s*([^)]+))?\)$/);
  if (!match) return trimmed;

  const resolved = cachedThemeColor(trimmed, () =>
    styles.getPropertyValue(match[1]).trim(),
  );
  return resolved || match[2]?.trim() || trimmed;
}

function parseDasharray(value) {
  if (!value) return [];
  return value
    .split(/[\s,]+/)
    .map((part) => Number.parseFloat(part))
    .filter((part) => Number.isFinite(part) && part > 0);
}

function createCanvasRoot(width, height) {
  const canvas = document.createElement("canvas");
  const dpr = window.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(width * dpr));
  canvas.height = Math.max(1, Math.round(height * dpr));
  canvas.style.width = "100%";
  canvas.style.height = "100%";
  canvas.style.display = "block";

  const ctx = canvas.getContext("2d");
  if (!ctx) return { canvas, ctx: null };
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { canvas, ctx };
}

// Charts use monotone cubic interpolation (Fritsch–Carlson). Pure
// polylines read as too jagged at low bucket counts; quadratic-with-
// midpoint-control adds humps that aren't in the data. Monotone cubic
// smooths the joins between segments without introducing local extrema
// the samples don't support — the curve is guaranteed to stay between
// adjacent y values, so peaks and valleys are exactly where the data
// puts them.
//
// CURVE_TIGHTNESS scales how far the cubic control points sit from
// each data point. 1.0 = full monotone cubic (control offset = dx/3,
// the canonical value). 0 = collapsed to lineTo. A value below 1
// keeps the smoothing-at-joins behaviour but makes the segment
// approach the next sample more directly — the curve hugs the data
// instead of bowing.
const CURVE_TIGHTNESS = 0.6;

function computeMonotoneTangents(points) {
  const n = points.length;
  if (n < 2) return new Array(n).fill(0);
  const slopes = new Array(n - 1);
  for (let i = 0; i < n - 1; i += 1) {
    const dx = points[i + 1].x - points[i].x;
    slopes[i] = dx === 0 ? 0 : (points[i + 1].y - points[i].y) / dx;
  }
  const tangents = new Array(n);
  tangents[0] = slopes[0];
  tangents[n - 1] = slopes[n - 2];
  for (let i = 1; i < n - 1; i += 1) {
    const s0 = slopes[i - 1];
    const s1 = slopes[i];
    // Sign-change at this point ⇒ local extremum: clamp tangent to 0
    // so the interpolant doesn't overshoot it.
    if (s0 * s1 <= 0) {
      tangents[i] = 0;
    } else {
      const dx0 = points[i].x - points[i - 1].x;
      const dx1 = points[i + 1].x - points[i].x;
      const w0 = 2 * dx1 + dx0;
      const w1 = dx1 + 2 * dx0;
      tangents[i] = (w0 + w1) / (w0 / s0 + w1 / s1);
    }
  }
  return tangents;
}

function createCurvePath2D(points) {
  const path = new Path2D();
  if (!points.length) return path;
  path.moveTo(points[0].x, points[0].y);
  appendCurveSegments(path, points);
  return path;
}

function appendCurveSegments(path, points) {
  if (points.length < 2) return;
  const tangents = computeMonotoneTangents(points);
  for (let i = 1; i < points.length; i += 1) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const dx = ((p1.x - p0.x) / 3) * CURVE_TIGHTNESS;
    path.bezierCurveTo(
      p0.x + dx, p0.y + tangents[i - 1] * dx,
      p1.x - dx, p1.y - tangents[i]     * dx,
      p1.x,      p1.y,
    );
  }
}

// Walks `points` right-to-left, drawing the same monotone cubic
// segments as the forward pass — just with control points swapped so
// the reverse traversal traces the identical curve. Used as the
// bottom edge of stacked-area bands; if forward and reverse picked
// different curves between the same samples the closed shape would
// self-intersect.
function appendReverseCurveSegments(path, points) {
  if (points.length < 2) return;
  const tangents = computeMonotoneTangents(points);
  for (let i = points.length - 1; i > 0; i -= 1) {
    const p0 = points[i - 1];
    const p1 = points[i];
    const dx = ((p1.x - p0.x) / 3) * CURVE_TIGHTNESS;
    // Forward draws (c1=fwd1, c2=fwd2, end=p1); reverse draws from p1
    // to p0 along the same curve, which is bezierCurveTo(fwd2, fwd1, p0).
    path.bezierCurveTo(
      p1.x - dx, p1.y - tangents[i]     * dx,
      p0.x + dx, p0.y + tangents[i - 1] * dx,
      p0.x,      p0.y,
    );
  }
}

function createAreaPathToBaseline(points, baselineY) {
  const path = new Path2D();
  if (!points.length) return path;

  path.moveTo(points[0].x, points[0].y);
  appendCurveSegments(path, points);
  path.lineTo(points[points.length - 1].x, baselineY);
  path.lineTo(points[0].x, baselineY);
  path.closePath();
  return path;
}

function createAreaBetweenPath2D(topPoints, bottomPoints) {
  const path = new Path2D();
  if (!topPoints.length || !bottomPoints.length) return path;

  path.moveTo(topPoints[0].x, topPoints[0].y);
  appendCurveSegments(path, topPoints);

  const lastBottom = bottomPoints[bottomPoints.length - 1];
  path.lineTo(lastBottom.x, lastBottom.y);
  appendReverseCurveSegments(path, bottomPoints);
  path.closePath();
  return path;
}

function drawCanvasGrid(ctx, width, height, gridConfig = {}, styles) {
  const {
    count = 4,
    stroke = "currentColor",
    opacity = 0.12,
    dasharray = "3 8",
  } = gridConfig;

  ctx.save();
  ctx.strokeStyle = resolveCanvasColor(stroke, styles) || stroke;
  ctx.globalAlpha = opacity;
  ctx.lineWidth = 1;
  const dash = parseDasharray(dasharray);
  ctx.setLineDash(dash);

  for (let line = 1; line <= count; line += 1) {
    const y = (height / (count + 1)) * line + 0.5;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }

  ctx.restore();
}

function pathBounds(points, padding, width, height) {
  if (!points.length) {
    return {
      left: padding,
      top: padding,
      right: width - padding,
      bottom: height - padding,
    };
  }

  return {
    left: Math.max(0, Math.min(...points.map((point) => point.x)) - 6),
    top: Math.max(0, Math.min(...points.map((point) => point.y)) - 6),
    right: Math.min(width, Math.max(...points.map((point) => point.x)) + 6),
    bottom: Math.min(height, Math.max(...points.map((point) => point.y)) + 6),
  };
}

// Offscreen dot-tile cache for the area fills below. The previous
// implementation drew every dot as an individual arc()+fill() inside a
// Path2D clip — on a full-width overview chart (~1600×250 CSS px at the
// default 8px grid) that's ~6k clipped paint ops per layer, ~30k per
// redraw with five agent layers. Weak GPUs spent 100ms+ of raster per
// settled redraw on it; toggling the stacked layers off via the legend
// made resize visibly faster, which is how it was found (O-1996). A
// cached CanvasPattern is one paint op per layer.
//
// Tiles render at devicePixelRatio resolution and the pattern is scaled
// back down so dots stay crisp under the chart's dpr transform; the
// cache key carries every input that changes the tile's pixels. Entries
// are never evicted — the key space is bounded by the palette × a few
// size/radius presets.
const _dotTilePatterns = new Map();

export function getDotTilePattern(ctx, { color, size, dotX, dotY, dotRadius }) {
  if (typeof document === "undefined") return null;
  const canScale =
    typeof DOMMatrix !== "undefined" &&
    typeof CanvasPattern !== "undefined" &&
    "setTransform" in CanvasPattern.prototype;
  // Without pattern transforms (very old engines), render the tile at
  // CSS resolution — slightly soft on hidpi, but still one paint op.
  const dpr = canScale ? window.devicePixelRatio || 1 : 1;
  const key = `${color}|${size}|${dotX}|${dotY}|${dotRadius}|${dpr}`;
  let pattern = _dotTilePatterns.get(key);
  if (pattern !== undefined) return pattern;

  const tile = document.createElement("canvas");
  tile.width = Math.max(1, Math.round(size * dpr));
  tile.height = Math.max(1, Math.round(size * dpr));
  const tileCtx = tile.getContext("2d");
  if (!tileCtx) {
    _dotTilePatterns.set(key, null);
    return null;
  }
  tileCtx.scale(dpr, dpr);
  tileCtx.fillStyle = color;
  tileCtx.beginPath();
  tileCtx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2);
  tileCtx.fill();

  pattern = ctx.createPattern(tile, "repeat") || null;
  if (pattern && dpr !== 1) {
    pattern.setTransform(new DOMMatrix().scale(1 / dpr));
  }
  _dotTilePatterns.set(key, pattern);
  return pattern;
}

function fillDotsInPath(ctx, path, bounds, options = {}) {
  const {
    color = "currentColor",
    opacity = 0.55,
    // Dot-grid spacing / radius for the area stipple — shared preset (lib/stipple.js)
    // so every stipple surface tracks one density. 8px read as too coarse on the
    // big surfaces; the dense preset reads as a continuous texture, not confetti.
    size = STIPPLE_SIZE,
    dotX = 2,
    dotY = 2,
    dotRadius = STIPPLE_DOT_RADIUS,
  } = options;

  const pattern = getDotTilePattern(ctx, { color, size, dotX, dotY, dotRadius });
  ctx.save();
  ctx.globalAlpha = opacity;
  if (pattern) {
    // Pattern space anchors at the canvas origin (not per-layer bounds
    // like the loop below), so overlapping layers share one aligned dot
    // grid — a deliberate visual simplification, not drift.
    ctx.fillStyle = pattern;
    ctx.fill(path);
    ctx.restore();
    return;
  }

  // Fallback when a pattern can't be built: the original per-dot loop.
  ctx.clip(path);
  ctx.fillStyle = color;
  for (let y = bounds.top + dotY; y < bounds.bottom + size; y += size) {
    for (let x = bounds.left + dotX; x < bounds.right + size; x += size) {
      ctx.beginPath();
      ctx.arc(x, y, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  ctx.restore();
}

function strokeCanvasCurve(ctx, points, options = {}) {
  const {
    stroke = "currentColor",
    strokeWidth = 2.5,
    strokeOpacity = 1,
    haloStroke = null,
    haloWidth = strokeWidth + 2.4,
    haloOpacity = 1,
    lineDash = null,
  } = options;

  const path = createCurvePath2D(points);

  if (haloStroke) {
    ctx.save();
    ctx.strokeStyle = haloStroke;
    ctx.globalAlpha = haloOpacity;
    ctx.lineWidth = haloWidth;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    // Halo always solid so dashed dashes don't lose their soft edge.
    ctx.stroke(path);
    ctx.restore();
  }

  ctx.save();
  ctx.strokeStyle = stroke;
  ctx.globalAlpha = strokeOpacity;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = lineDash ? "butt" : "round";
  ctx.lineJoin = "round";
  if (lineDash) ctx.setLineDash(lineDash);
  ctx.stroke(path);
  ctx.restore();
}

// Dot at sample point(s) — used by the hover overlay to highlight only
// the two endpoints of the segment under the cursor, so the eye can
// tell a real sample from an interpolated arc without the chart being
// littered with always-on dots.
export function drawCanvasDataPoints(ctx, points, options = {}) {
  const {
    fill = "currentColor",
    radius = 1.6,
    opacity = 0.85,
    haloFill = null,
    haloRadius = radius + 1.4,
    haloOpacity = 1,
  } = options;
  if (!points || points.length === 0) return;
  if (haloFill) {
    ctx.save();
    ctx.fillStyle = haloFill;
    ctx.globalAlpha = haloOpacity;
    for (const p of points) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, haloRadius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  ctx.save();
  ctx.fillStyle = fill;
  ctx.globalAlpha = opacity;
  for (const p of points) {
    ctx.beginPath();
    ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

export function createSurfaceChart(options = {}) {
  const {
    mode = "line",
    series = [],
    layers = [],
    width = 720,
    height = 220,
    padding = 18,
    preserveAspectRatio = "none",
    className = "",
    domainMin = 0,
    domainMax = null,
    grid = false,
    gridConfig = {},
    stroke = "currentColor",
    strokeWidth = 2.5,
    fill = null,
    fillPattern = false,
    fillPatternColor = null,
    fillPatternSize = null,
    fillDotRadius = null,
    fillOpacity = 0.7,
    totalStroke = stroke,
    totalStrokeWidth = 2.5,
    showTotalLine = true,
    layerDefaults = {},
    lineHalo = true,
    haloStroke = "var(--plot-bg)",
    haloWidth = null,
    minLayerHeight = 0,
    // Faint outlines drawn behind the main chart so the scoped view shows
    // its relative scale against ancestor scopes (unscoped tenant, parent,
    // and any user-enabled intermediates). Each entry: { series, stroke?,
    // strokeOpacity?, lineDash? }. Y-domain ceiling is computed upstream
    // in SurfaceChart from the max across all reference series.
    referenceSeriesList = null,
    referenceStroke = "var(--muted)",
  } = options;
  const refList = Array.isArray(referenceSeriesList)
    ? referenceSeriesList.filter((r) => Array.isArray(r?.series) && r.series.length > 0)
    : [];
  const hasReference = refList.length > 0;

  const { canvas, ctx } = createCanvasRoot(width, height);
  if (!ctx) return canvas;
  const styles = getComputedStyle(document.documentElement);
  const resolvedHaloStroke = resolveCanvasColor(haloStroke, styles);

  if (grid) drawCanvasGrid(ctx, width, height, gridConfig, styles);

  if (mode === "stacked" && layers.length) {
    const rawTotals = sumStackedLayers(layers);
    // When one layer dominates (e.g., 95%+ share) the others collapse to
    // sub-pixel slivers. `minLayerHeight` lifts every active layer to a
    // floor expressed in pixels, then we recompute totals so the white
    // envelope traces the *visual* stack top — keeps "drawing outside the
    // line" from creeping back in.
    const chartH = Math.max(1, height - 2 * padding);
    const rawMax = Math.max(...rawTotals, 1);
    const minLayerHval = minLayerHeight > 0 ? (minLayerHeight * rawMax) / chartH : 0;
    const drawnSeries = minLayerHval > 0
      ? layers.map((layer) =>
          layer.series.map((v) => (v > 0 ? Math.max(minLayerHval, v) : 0)),
        )
      : layers.map((layer) => layer.series);
    const totals = minLayerHval > 0
      ? rawTotals.map((_, i) => drawnSeries.reduce((sum, s) => sum + s[i], 0))
      : rawTotals;
    const drawnLayers = minLayerHval > 0
      ? layers.map((layer, i) => ({ ...layer, series: drawnSeries[i] }))
      : layers;
    const { min, max } = resolveSurfaceDomain({
      layers: drawnLayers,
      domainMin,
      domainMax,
    });

    // Reference outlines go down FIRST so the active layers and the
    // total line draw over them — they're context, not foreground.
    // Faintest first so the deepest ancestor (parent) reads on top.
    if (hasReference) {
      for (const ref of refList) {
        const refPoints = seriesPoints(ref.series, width, height, padding, min, max);
        strokeCanvasCurve(ctx, refPoints, {
          stroke: resolveCanvasColor(ref.stroke || referenceStroke, styles),
          strokeWidth: totalStrokeWidth,
          strokeOpacity: ref.strokeOpacity ?? 0.35,
          lineDash: ref.lineDash || null,
          haloStroke: lineHalo ? resolvedHaloStroke : null,
          haloWidth: haloWidth ?? totalStrokeWidth + 2.4,
          haloOpacity: 0.6,
        });
      }
    }

    let previous = Array.from({ length: totals.length }, () => 0);

    drawnLayers.forEach((layer, index) => {
      const cumulative = layer.series.map((value, pointIndex) => value + previous[pointIndex]);
      const topPoints = seriesPoints(cumulative, width, height, padding, min, max);
      const bottomPoints = seriesPoints(previous, width, height, padding, min, max);
      const isTopLayer = index === layers.length - 1;
      const areaPath = createAreaBetweenPath2D(topPoints, bottomPoints);
      const bounds = pathBounds([...topPoints, ...bottomPoints], padding, width, height);
      fillDotsInPath(ctx, areaPath, bounds, {
        color: resolveCanvasColor(layer.fillPatternColor || layer.color, styles),
        opacity:
          layer.dotOpacity ??
          (isTopLayer ? layerDefaults.topDotOpacity ?? 0.6 : layerDefaults.dotOpacity ?? 0.46),
        // Dense stipple from the shared preset (lib/stipple.js) — 8px read as
        // too coarse/patchy on the big stacked area surfaces. The top layer's
        // dots are a touch larger (deliberate stacking emphasis).
        size: layer.patternSize || layerDefaults.patternSize || STIPPLE_SIZE,
        dotRadius:
          layer.dotRadius ??
          (isTopLayer ? layerDefaults.topDotRadius ?? STIPPLE_DOT_RADIUS + 0.1 : layerDefaults.dotRadius ?? STIPPLE_DOT_RADIUS),
      });

      strokeCanvasCurve(ctx, topPoints, {
        stroke: resolveCanvasColor(layer.stroke || layer.color, styles),
        strokeWidth:
          layer.strokeWidth ??
          (isTopLayer
            ? layerDefaults.topStrokeWidth ?? 1.9
            : layerDefaults.strokeWidth ?? 1.1),
        haloStroke:
          resolveCanvasColor(
            layer.haloStroke ??
              layerDefaults.haloStroke ??
              (lineHalo ? haloStroke : null),
            styles,
          ),
        haloWidth:
          layer.haloWidth ??
          (isTopLayer
            ? layerDefaults.topHaloWidth ?? layerDefaults.haloWidth ?? 4.2
            : layerDefaults.haloWidth ?? 3.1),
        strokeOpacity:
          layer.strokeOpacity ??
          (isTopLayer
            ? layerDefaults.topStrokeOpacity ?? 0.72
            : layerDefaults.strokeOpacity ?? 0.48),
      });

      previous = cumulative;
    });

    const totalPoints = seriesPoints(totals, width, height, padding, min, max);
    if (showTotalLine) {
      strokeCanvasCurve(ctx, totalPoints, {
        stroke: resolveCanvasColor(totalStroke, styles),
        strokeWidth: totalStrokeWidth,
        haloStroke: lineHalo ? resolvedHaloStroke : null,
        haloWidth: haloWidth ?? totalStrokeWidth + 2.4,
      });
    }

    return canvas;
  }

  const { min, max } = resolveSurfaceDomain({
    series,
    domainMin: Math.min(domainMin, Math.min(...series, domainMin)),
    domainMax,
  });
  const linePoints = seriesPoints(series, width, height, padding, min, max);
  const baselineY = height - padding;

  if (hasReference) {
    for (const ref of refList) {
      const refPoints = seriesPoints(ref.series, width, height, padding, min, max);
      strokeCanvasCurve(ctx, refPoints, {
        stroke: resolveCanvasColor(ref.stroke || referenceStroke, styles),
        strokeWidth: totalStrokeWidth,
        strokeOpacity: ref.strokeOpacity ?? 0.35,
        lineDash: ref.lineDash || null,
        haloStroke: lineHalo ? resolvedHaloStroke : null,
        haloWidth: haloWidth ?? totalStrokeWidth + 2.4,
        haloOpacity: 0.6,
      });
    }
  }

  if (fill) {
    if (fillPattern) {
      const areaPath = createAreaPathToBaseline(linePoints, baselineY);
      const bounds = pathBounds(
        [
          ...linePoints,
          { x: linePoints[linePoints.length - 1]?.x ?? width - padding, y: baselineY },
          { x: linePoints[0]?.x ?? padding, y: baselineY },
        ],
        padding,
        width,
        height,
      );
      fillDotsInPath(ctx, areaPath, bounds, {
        color: resolveCanvasColor(fillPatternColor || stroke, styles),
        opacity: 0.55,
        ...(fillPatternSize ? { size: fillPatternSize } : {}),
        ...(fillDotRadius ? { dotRadius: fillDotRadius } : {}),
      });
    } else {
      const areaPath = createAreaPathToBaseline(linePoints, baselineY);
      ctx.save();
      ctx.fillStyle = resolveCanvasColor(fill, styles);
      ctx.globalAlpha = fillOpacity;
      ctx.fill(areaPath);
      ctx.restore();
    }
  }

  strokeCanvasCurve(ctx, linePoints, {
    stroke: resolveCanvasColor(stroke, styles),
    strokeWidth,
    haloStroke: lineHalo ? resolvedHaloStroke : null,
    haloWidth: haloWidth ?? strokeWidth + 2.4,
  });

  return canvas;
}

